// Organization search that treats spelled-out numbers and digits, "&" and "and", and common short forms as the same:
// "3 and 20" finds "Three and Twenty Fire Department", "96" finds "Ninety-six Elementary", "fd" or "fire" finds "Fire Department".
// The same file is in apps/api/src/lib/orgSearch.ts (import organization suggestions); keep them identical.
const UNITS: Record<string, number> = { zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19 };
const TENS: Record<string, number> = { twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };
const ORD: Record<string, string> = { first: '1st', second: '2nd', third: '3rd', fourth: '4th', fifth: '5th' };
// Longer phrases first: each becomes the short form.
const SHORT: [RegExp, string][] = [
  [/\bfire (department|dept|dpt)\b/g, 'fd'], [/\bfire rescue\b/g, 'fr'], [/\bvolunteer fd\b/g, 'vfd'],
  [/\bpolice (department|dept|dpt)\b/g, 'pd'], [/\bsheriff'?s? (office|department|dept)\b/g, 'so'],
  [/\bdepartment\b/g, 'dept'], [/\bcounty\b/g, 'co'], [/\bcnty\b/g, 'co'], [/\belementary\b/g, 'elem'],
  [/\bschool district\b/g, 'sd'], [/\bsaint\b/g, 'st'], [/\bmount\b/g, 'mt'], [/\bfort\b/g, 'ft'],
];
// Short forms written out again, so a partial word typed ("fire", "county", "police") also matches "FD", "Co.", "PD".
const LONG: [RegExp, string][] = [
  [/\bvfd\b/g, 'volunteer fire department vfd'], [/\bfd\b/g, 'fire department fd'], [/\bfr\b/g, 'fire rescue fr'],
  [/\bpd\b/g, 'police department pd'], [/\bso\b/g, 'sheriff office so'], [/\bdept\b/g, 'department dept'], [/\bco\b/g, 'county co'],
  [/\belem\b/g, 'elementary elem'], [/\bsd\b/g, 'school district sd'],
];

export function normOrg(s: string): string {
  const words = s.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim().split(' ');
  // Spelled-out numbers -> digits ("ninety six" -> 96, "three" -> 3, "first" -> 1st).
  const out: string[] = [];
  for (let i = 0; i < words.length; i++) {
    const w = words[i]!;
    if (w in TENS) {
      const next = words[i + 1];
      if (next && next in UNITS && UNITS[next]! < 10) { out.push(String(TENS[w]! + UNITS[next]!)); i++; continue; }
      out.push(String(TENS[w])); continue;
    }
    if (w in UNITS) { out.push(String(UNITS[w])); continue; }
    if (w in ORD) { out.push(ORD[w]!); continue; }
    out.push(w);
  }
  let t = ` ${out.join(' ')} `;
  for (const [re, short] of SHORT) t = t.replace(re, short);
  return t.replace(/\s+/g, ' ').trim();
}

/** Does the organization name match what was typed? Every typed word must start a word of the name (any order). */
export function orgMatches(name: string, query: string): boolean {
  const q = normOrg(query);
  if (!q) return true;
  const short = normOrg(name);
  let long = short;
  for (const [re, l] of LONG) long = long.replace(re, l);
  const n = ` ${short} ${long} `;
  return n.includes(` ${q} `) || q.split(' ').every((w) => n.includes(` ${w}`));
}
